import React, {
  useEffect,
  useState,
  useRef,
  MouseEvent,
  useCallback,
} from 'react';
import { useAppDispatch, RootState } from '../../../store';
import { useSelector } from 'react-redux';
import { fetchStructure, fetchSubFolderAndFiles } from '../../../store/actions/FolderAction';
import {
  PiCaretLeftLight,
  PiCaretRightLight,
  PiFolderLight,
  PiFileLight,
} from 'react-icons/pi';
import { FileProps, FolderProps } from '../../Files/documents/types';
import Button from './Button';
import { useTranslation } from 'react-i18next';

export interface VaultFile {
  id: string;
  name: string;
  url?: string;
}

interface VaultPickerProps {
  onFilesSelected: (files: VaultFile[]) => void;
}

type VaultItem = {
  id: string;
  name: string;
  type: 'folder' | 'file';
  folderData?: FolderProps;
  fileData?: FileProps;
};

const VaultPicker: React.FC<VaultPickerProps> = ({ onFilesSelected }) => {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();

  const structure = useSelector((state: RootState) => state.folders.structure || []);

  const [pathStack, setPathStack] = useState<FolderProps[]>([]);
  const [currentItems, setCurrentItems] = useState<VaultItem[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const itemRefs = useRef<Record<string, HTMLDivElement | null>>({});
  const containerRef = useRef<HTMLDivElement | null>(null);
  const [isSelecting, setIsSelecting] = useState(false);
  const [selectionStart, setSelectionStart] = useState<{ x: number; y: number } | null>(null);
  const [selectionEnd, setSelectionEnd] = useState<{ x: number; y: number } | null>(null);

  useEffect(() => {
    setIsLoading(true);
    // dispatch(fetchStructure()).finally(() => setIsLoading(false));
  }, [dispatch]);

  useEffect(() => {
    if (pathStack.length === 0 && structure.length > 0) {
      const rootItems: VaultItem[] = structure.map((folder: FolderProps) => ({
        id: folder.FolderID,
        name: folder.FolderName,
        type: 'folder',
        folderData: folder,
      }));
      setCurrentItems(rootItems);
    }
  }, [structure, pathStack]);

  const loadFolderContent = async (folder: FolderProps) => {
    try {
      setIsLoading(true);
      const result: any = await dispatch(fetchSubFolderAndFiles({ folderID: folder.FolderID }));
      const { subfolders = [], files = [] } = result;
      const items: VaultItem[] = [];

      subfolders.forEach((sf: FolderProps) => {
        items.push({
          id: sf.FolderID,
          name: sf.FolderName,
          type: 'folder',
          folderData: sf,
        });
      });

      files.forEach((file: FileProps) => {
        items.push({
          id: file.FileID,
          name: file.FileName,
          type: 'file',
          fileData: file,
        });
      });

      setCurrentItems(items);
    } catch (error) {
      console.error('Error loading folder content:', error);
    } finally {
      setIsLoading(false);
      setSelectedIds(new Set());
    }
  };

  const handleBack = () => {
    if (pathStack.length === 0) return;
    const newStack = [...pathStack];
    newStack.pop();
    setPathStack(newStack);

    if (newStack.length === 0) {
      const rootItems: VaultItem[] = structure.map((folder: FolderProps) => ({
        id: folder.FolderID,
        name: folder.FolderName,
        type: 'folder',
        folderData: folder,
      }));
      setCurrentItems(rootItems);
      setSelectedIds(new Set());
    } else {
      loadFolderContent(newStack[newStack.length - 1]);
    }
  };

  const handleBreadcrumbClick = (index: number) => {
    if (index < 0) {
      setPathStack([]);
      const rootItems: VaultItem[] = structure.map((folder: FolderProps) => ({
        id: folder.FolderID,
        name: folder.FolderName,
        type: 'folder',
        folderData: folder,
      }));
      setCurrentItems(rootItems);
      setSelectedIds(new Set());
    } else {
      const newStack = pathStack.slice(0, index + 1);
      setPathStack(newStack);
      loadFolderContent(newStack[newStack.length - 1]);
    }
  };

  const handleItemClick = (e: MouseEvent, item: VaultItem) => {
    if (item.type === 'folder' && item.folderData) {
      setSelectedIds(new Set());
      const newStack = [...pathStack, item.folderData];
      setPathStack(newStack);
      loadFolderContent(item.folderData);
      return;
    }

    if (item.type === 'file') {
      const ctrlOrCmd = e.metaKey || e.ctrlKey;
      setSelectedIds((prev) => {
        const newSet = new Set(prev);
        if (ctrlOrCmd) {
          if (newSet.has(item.id)) newSet.delete(item.id);
          else newSet.add(item.id);
        } else {
          newSet.clear();
          newSet.add(item.id);
        }
        return newSet;
      });
    }
  };

  const getContainerCoords = (e: React.MouseEvent) => {
    if (!containerRef.current) return { x: e.clientX, y: e.clientY };
    const rect = containerRef.current.getBoundingClientRect();
    return {
      x: e.clientX - rect.left + containerRef.current.scrollLeft,
      y: e.clientY - rect.top + containerRef.current.scrollTop,
    };
  };

  const handleMouseDown = (e: React.MouseEvent<HTMLDivElement>) => {
    if (e.target instanceof HTMLElement && e.target.closest('.vault-item')) {
      return;
    }
    if (e.button !== 0) return;

    setIsSelecting(true);
    const coords = getContainerCoords(e);
    setSelectionStart(coords);
    setSelectionEnd(coords);
  };

  const handleMouseMove = (e: React.MouseEvent<HTMLDivElement>) => {
    if (!isSelecting || !selectionStart) return;
    const coords = getContainerCoords(e);
    setSelectionEnd(coords);
  };

  const handleMouseUp = (e: React.MouseEvent<HTMLDivElement>) => {
    if (!isSelecting || !selectionStart || !selectionEnd) {
      setIsSelecting(false);
      return;
    }
    setIsSelecting(false);

    const ctrlOrCmd = e.metaKey || e.ctrlKey;
    const newSelection = new Set(selectedIds);

    const minX = Math.min(selectionStart.x, selectionEnd.x);
    const maxX = Math.max(selectionStart.x, selectionEnd.x);
    const minY = Math.min(selectionStart.y, selectionEnd.y);
    const maxY = Math.max(selectionStart.y, selectionEnd.y);

    if (containerRef.current) {
      const containerRect = containerRef.current.getBoundingClientRect();

      currentItems.forEach((item) => {
        if (item.type === 'file') {
          const el = itemRefs.current[item.id];
          if (!el) return;
          const rect = el.getBoundingClientRect();

          const itemLeft = rect.left - containerRect.left + containerRef.current!.scrollLeft;
          const itemRight = rect.right - containerRect.left + containerRef.current!.scrollLeft;
          const itemTop = rect.top - containerRect.top + containerRef.current!.scrollTop;
          const itemBottom = rect.bottom - containerRect.top + containerRef.current!.scrollTop;

          const overlap = itemLeft < maxX && itemRight > minX && itemTop < maxY && itemBottom > minY;

          if (overlap) {
            if (ctrlOrCmd) {
              if (newSelection.has(item.id)) newSelection.delete(item.id);
              else newSelection.add(item.id);
            } else {
              newSelection.add(item.id);
            }
          } else if (!ctrlOrCmd) {
            newSelection.delete(item.id);
          }
        }
      });
    }

    setSelectedIds(newSelection);
    setSelectionStart(null);
    setSelectionEnd(null);
  };

  const setItemRef = useCallback(
    (id: string) => (el: HTMLDivElement | null) => {
      itemRefs.current[id] = el;
    },
    []
  );

  const getIcon = (type: 'folder' | 'file') => {
    return type === 'folder'
      ? <PiFolderLight className="h-8 w-8 text-info-500" />
      : <PiFileLight className="h-8 w-8 text-success-500" />;
  };

  const renderBreadcrumbs = () => (
    <div className="flex items-center space-x-2 mb-4">
      {pathStack.length > 0 && (
        <button
          onClick={handleBack}
          className="text-primary-500 hover:underline flex items-center"
        >
          <PiCaretLeftLight className="h-5 w-5" />
        </button>
      )}
      <span
        className="text-fg-muted text-xs cursor-pointer hover:text-champagne hover:underline transition-colors"
        onClick={() => handleBreadcrumbClick(-1)}
      >
        {t('Vault')}
      </span>

      {pathStack.map((folder, idx) => (
        <div key={folder.FolderID} className="flex items-center space-x-1">
          <PiCaretRightLight className="h-5 w-5 text-fg-muted" />
          <span
            className="text-fg-muted hover:text-champagne hover:underline cursor-pointer text-xs truncate max-w-52 transition-colors"
            onClick={() => handleBreadcrumbClick(idx)}
          >
            {folder.FolderName}
          </span>
        </div>
      ))}
    </div>
  );

  const renderSkeletons = () => (
    <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-3 lg:grid-cols-4 gap-4">
      {Array.from({ length: 4 }).map((_, i) => (
        <div
          key={i}
          className="animate-fadePulse flex flex-col items-center p-4 bg-surface-sunken/70 rounded-lg 
                     shadow-sm border border-hairline"
        >
          <div className="w-8 h-8 mb-2 bg-gray-300 rounded" />
          <div className="w-3/4 h-3 bg-gray-200 rounded" />
        </div>
      ))}
    </div>
  );

  const selectedFiles: VaultFile[] = currentItems
  .filter((item) => item.type === 'file' && selectedIds.has(item.id))
  .map((item) => ({
    id: item.fileData?.FileID || '',
    name: item.fileData?.FileName || '',
    url: item.fileData?.FileSasURL || '',
  }));

  const handleAttachFiles = () => {
    onFilesSelected(selectedFiles);
  };
  let selectionBox: JSX.Element | null = null;
  if (isSelecting && selectionStart && selectionEnd) {
    const left = Math.min(selectionStart.x, selectionEnd.x);
    const top = Math.min(selectionStart.y, selectionEnd.y);
    const width = Math.abs(selectionEnd.x - selectionStart.x);
    const height = Math.abs(selectionEnd.y - selectionStart.y);

    selectionBox = (
      <div
        style={{
          position: 'absolute',
          zIndex: 9999,
          pointerEvents: 'none',
          left,
          top,
          width,
          height,
          backgroundColor: 'rgba(100, 149, 237, 0.2)',
          border: '1px solid rgba(100, 149, 237, 0.6)',
        }}
      />
    );
  }

  return (
    <div
      ref={containerRef}
      className="relative p-4 sm:w-full md:w-[600px] max-w-screen-lg mx-auto h-[70vh] overflow-y-auto"
      onMouseDown={handleMouseDown}
      onMouseMove={handleMouseMove}
      onMouseUp={handleMouseUp}
    >
      <div className="flex items-center justify-between mb-2">
        {renderBreadcrumbs()}
      </div>

      {isLoading ? (
        renderSkeletons()
      ) : currentItems.length === 0 ? (
        <div className="text-center text-fg-muted mt-10">
          {t('No folders or files here')}
        </div>
      ) : (
        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-3 lg:grid-cols-4 gap-4">
          {currentItems.map((item) => {
            const isSelected = selectedIds.has(item.id) && item.type === 'file';
            return (
              <div
                key={item.id}
                ref={setItemRef(item.id)}
                onClick={(e) => handleItemClick(e, item)}
                className={`vault-item flex flex-col items-center p-4 bg-surface-sunken/70 rounded-lg 
                  shadow-sm border border-hairline transition-shadow
                  cursor-pointer hover:shadow-md
                  ${isSelected ? 'ring-2 ring-info-400 bg-info-50' : ''}`}
              >
                {getIcon(item.type)}
                <p className="mt-2 text-sm text-center text-fg-body truncate w-full">
                  {item.name}
                </p>
              </div>
            );
          })}
        </div>
      )}
      {selectedFiles.length > 0 && (
        <div className="flex justify-end mt-5">
          <Button
            onClick={handleAttachFiles}
            variant="primary"
            size="small"
          >
            Attach {selectedFiles.length} File{selectedFiles.length > 1 ? 's' : ''}
          </Button>
        </div>
      )}
      {selectionBox}
    </div>
  );
};

export default VaultPicker;
